import PageHeading from "components/PageHeading.component";
import {
  Card,
  ConfigProvider,
  Flex,
  Image,
  Table,
  TableProps,
  Typography,
  InputRef,
  Avatar,
} from "antd";
import { UserOutlined } from "@ant-design/icons";
import { User } from "shared/types";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { useRef, useState } from "react";
import { FilterValue } from "antd/lib/table/interface";
import NewKaryawanModal from "components/modals/NewKaryawanModal.component";
import { getAllStaffByBusinessId } from "hooks/queries/staff";
import { useBusinessSettings } from "hooks/queries/business-settings";
import { PAGE_SIZE } from "shared/constants";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { KARYAWAN_STAFF_TYPE, UserType, karyawanTypeOf } from "shared/types/enum";
import FileUpload from "components/buttons/CsvUpload.component";
import { modalButtonsTheme } from "components/modals/ModalChrome";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import StatBand from "components/StatBand.component";
import { th, tnum } from "shared/components/TableText";
import { translate } from "shared/languages";
import { formatPhone } from "shared/utils/phone";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import {
  ACCOUNT_STATUS_LABELS,
  ACCOUNT_STATUS_TONE,
  ASSIGNED_CLASSES_LABELS,
  assignedClassesLabel,
} from "shared/utils";

// The band's segments mirror the table's Status filter values; "all" means
// no filter (both statuses visible).
type StatusSegment = "all" | "active" | "deactivated";
type KaryawanType = UserType.Instructor | UserType.Staff;

// One tab's worth of the Karyawan page: the stat band, the toolbar and the
// table for ONE employee type. Each tab owns its own status filter, column
// searches and page memory, so switching tabs never carries a filter across.
const KaryawanTable = ({
  type,
  rows,
  isLoading,
  showAssignedClasses,
}: {
  type: KaryawanType;
  rows: User[];
  isLoading: boolean;
  showAssignedClasses: boolean;
}) => {
  const navigate = useNavigate();
  const isInstructor = type === UserType.Instructor;
  const { page, handleTableChange } = useRememberedTablePage(
    isInstructor ? "instructors" : "staff",
  );

  // Deactivation lives on staff.deleted_at, the same field the Status column
  // filters on, so the band and table can never disagree (the user row's
  // deleted_at is always null on this list, see the column).
  const totalCount = rows.length;
  const inactiveCount = rows.filter((user) => !!user.staff?.deleted_at).length;
  const activeCount = totalCount - inactiveCount;

  // Single source of truth for the Status column's filteredValue; the band
  // highlight and the table both read it. Fresh visits keep the page's
  // long-standing default: active people only.
  const [statusFilter, setStatusFilter] = useState<string[] | null>(["active"]);
  const activeSegment: StatusSegment =
    statusFilter?.length === 1 ? (statusFilter[0] as StatusSegment) : "all";

  const selectSegment = (key: StatusSegment) => {
    // Re-tapping the selected slice widens back to everyone.
    const next = key === "all" || key === activeSegment ? null : [key];
    setStatusFilter(next);
    // AntD auto-resets pagination only on dropdown-driven filter changes; a
    // controlled filteredValue change must reset the page itself.
    handleTableChange({ current: 1 });
  };

  const searchInput = useRef<InputRef>(null);

  // AntD runs a table's filters as either all-controlled or all-uncontrolled.
  // The band-driven column above is controlled, which flips the whole table
  // into controlled mode, so the search columns must be controlled as well or
  // AntD silently drops whatever the user types into them.
  const [searchFilters, setSearchFilters] = useState<Record<string, FilterValue | null>>({});

  // Employee-management at-a-glance overview, instructors only (class
  // assignment is an instructor rule). Only meaningful, and only fetched via
  // with_summary, once the studio has turned this on. Working hours / leave
  // counts stay in the profile's Ketersediaan tab; this list surfaces just
  // the assignment gap that affects scheduling.
  const employeeManagementColumns: NonNullable<TableProps<User>["columns"]> =
    isInstructor && showAssignedClasses
      ? [
          {
            title: th(translate("tabs.assigned_classes")),
            key: "classes",
            render: (_: unknown, user: User) => {
              const summary = user.staff?.employee_management_summary;
              const count = summary?.assignedClassCount ?? 0;
              // Checked first: assignedClassCount can never represent "all"
              // (there's no way to count infinity), so this must be an
              // explicit flag check, not inferred from the count.
              if (summary?.assignedAllClassTypes) {
                return (
                  <StatusBadge
                    tone="navy"
                    label={ASSIGNED_CLASSES_LABELS.all}
                    dot={false}
                  />
                );
              }
              return count > 0 ? (
                <StatusBadge
                  tone="navy"
                  label={assignedClassesLabel(count)}
                  dot={false}
                />
              ) : (
                <StatusBadge tone="neutral" label={ASSIGNED_CLASSES_LABELS.none} dot={false} />
              );
            },
          },
        ]
      : [];

  // Jabatan, Staf list only: free-text job title on the staff row. Searchable
  // like Nama; the shared search helper's default onFilter reads a flat
  // record key, so it is overridden to read the nested staff field. `key`
  // pins the filter key AntD reports ("job_title") for the controlled mirror.
  const jabatanColumns: NonNullable<TableProps<User>["columns"]> =
    type === UserType.Staff
      ? [
          {
            title: th(translate("label.job_title")),
            key: "job_title",
            dataIndex: ["staff", "job_title"],
            // "full_name" is borrowed only for the search UI shell (the
            // helper's config must name a real User key); the onFilter right
            // below replaces its default lookup with the nested staff field.
            ...getColumnSearchProps({ config: "full_name", searchInput }),
            onFilter: (value, record) =>
              (record.staff?.job_title ?? "").toLowerCase().includes(String(value).toLowerCase()),
            filteredValue: searchFilters.job_title ?? null,
            render: (_: unknown, record: User) => record.staff?.job_title || "-",
          },
        ]
      : [];

  const columns: TableProps<User>["columns"] = [
    {
      title: th(translate("label.name")),
      dataIndex: "full_name",
      render: (_, record) => (
        <Flex style={{ alignItems: "center" }} gap={10}>
          {record.photo_url ? (
            <Avatar src={<Image src={record.photo_url} alt="avatar" />} />
          ) : (
            <Avatar size={30} icon={<UserOutlined />} />
          )}
          <Typography.Text style={{ fontWeight: 600 }}>{record.full_name}</Typography.Text>
        </Flex>
      ),
      sorter: (a, b) => (a.full_name ?? "").localeCompare(b.full_name ?? ""),
      defaultSortOrder: "ascend",
      ...getColumnSearchProps({ config: "full_name", searchInput }),
      filteredValue: searchFilters.full_name ?? null,
    },
    ...jabatanColumns,
    {
      title: th(translate("label.phone_number")),
      dataIndex: "phone_number",
      key: "phone_number",
      ...getColumnSearchProps({
        config: { dataIndex: "phone_number", searchType: "phone" },
        searchInput,
      }),
      filteredValue: searchFilters.phone_number ?? null,
      // A non-teaching Staf may have no phone.
      render: (phone_number: string | null, _: User) => (
        <span style={tnum}>{phone_number ? formatPhone(phone_number) : "-"}</span>
      ),
    },
    ...employeeManagementColumns,
    {
      title: th("Status"),
      dataIndex: "deleted_at",
      key: "deleted_at",
      filters: [
        { text: ACCOUNT_STATUS_LABELS.active, value: "active" },
        { text: ACCOUNT_STATUS_LABELS.inactive, value: "deactivated" },
      ],
      // Deactivated people are hidden by default; the stat band (or this
      // dropdown) reveals them. Filter values must be strings: AntD
      // round-trips dropdown selections as string keys, so boolean values
      // never match once the user touches it.
      filteredValue: statusFilter,
      onFilter: (value, record) => (record.staff?.deleted_at ? "deactivated" : "active") === value,
      render: (_, user) =>
        user.staff?.deleted_at ? (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.inactive} label={ACCOUNT_STATUS_LABELS.inactive} />
        ) : (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.active} label={ACCOUNT_STATUS_LABELS.active} />
        ),
    },
  ];

  return (
    <Flex vertical gap={20}>
      {/* Timeless list facts only: each segment filters the table to its
          slice (re-tap to widen back to everyone), so "Tidak Aktif" people
          are one tap away instead of hiding behind the column-filter
          dropdown. */}
      <StatBand
        segments={[
          {
            key: "all",
            value: totalCount,
            label: translate(isInstructor ? "list_header.total_instructors" : "list_header.total_staff"),
          },
          // Status words follow the badges they filter (fixed English).
          { key: "active", value: activeCount, label: ACCOUNT_STATUS_LABELS.active },
          { key: "deactivated", value: inactiveCount, label: ACCOUNT_STATUS_LABELS.inactive },
        ]}
        selectedKey={activeSegment}
        onSelect={(key) => selectSegment(key as StatusSegment)}
      />
      <Card styles={{ body: { padding: 18 } }}>
        {/* Toolbar: Unggah CSV (instructors only; the import template is an
            instructor template) + the navy add CTA for this tab's type. The
            modal-buttons theme also gives the modal its ink Batal. */}
        <Flex justify={"flex-end"} gap={10} style={{ marginBottom: 14 }}>
          {isInstructor && <FileUpload endpoint={"staff"} templateUrl="/template-import-instruktur.csv" />}
          <ConfigProvider theme={modalButtonsTheme}>
            <NewKaryawanModal key={type} type={type} />
          </ConfigProvider>
        </Flex>
        <Table
          showSorterTooltip={false}
          sticky
          rowKey="id"
          size={"middle"}
          loading={isLoading}
          columns={columns}
          dataSource={rows}
          // `type` rides along so the sidebar keeps this list's item lit on
          // the detail page (see MainLayout's selectedKey).
          onRow={(record) => ({
            onClick: () => navigate(`${PATHS.STAFF}/${record.id}?type=${type}`),
            style: { cursor: "pointer" },
          })}
          pagination={{ current: page, pageSize: PAGE_SIZE, showSizeChanger: false }}
          onChange={(pagination, filters) => {
            handleTableChange(pagination);
            // Keep the band highlight in sync when the filter changes via
            // the column dropdown (fires on page/sort too; re-setting the
            // same value is a no-op).
            setSearchFilters(filters);
            setStatusFilter((filters.deleted_at as string[] | null) ?? null);
          }}
        />
      </Card>
    </Flex>
  );
};

// One Karyawan list per route, reached from the sidebar group Karyawan ->
// Instruktur / Staf (the same shape as Produk -> Paket / Membership), so
// teaching and non-teaching people are never mixed in one list and an admin
// works inside one type at a time. One fetch serves both routes (cached by
// react-query), split by karyawanTypeOf. Instructors keep their detail tabs;
// a non-teaching Staf has a profile only. The detail route stays /staff/:id.
const InstructorPage = ({ type }: { type: KaryawanType }) => {
  const { data: businessSettings } = useBusinessSettings();
  const employeeManagementEnabled = !!businessSettings?.employee_management_enabled;
  // The "Kelas yang Ditugaskan" column is the only employee-management data
  // this list surfaces, so it (and the summary fetch behind it) is gated on
  // that specific sub-flag, not the master alone. A studio using only Jam
  // Kerja/Cuti sees no extra column here.
  const classAssignmentEnabled =
    employeeManagementEnabled && !!businessSettings?.employee_management_class_assignment_enabled;
  // Omit the param entirely when off, rather than sending with_summary=false:
  // the backend parser is z.coerce.boolean(), which treats any non-empty
  // query string (including the literal "false") as true.
  const { data: karyawan = [], isLoading } = getAllStaffByBusinessId(
    KARYAWAN_STAFF_TYPE,
    classAssignmentEnabled ? { with_summary: true } : undefined,
  );
  const rows = karyawan.filter((u) => karyawanTypeOf(u.roles) === type);

  return (
    <>
      <PageHeading title={translate(type === UserType.Instructor ? "karyawan_type.instructor" : "karyawan_type.staff")} />
      {/* Keyed by type: both sidebar routes render this same component, so
          React would otherwise keep one instance alive across Instruktur ->
          Staf and carry its state over (the add modal's starting step, the
          status filter, column searches, the remembered page). A new key
          remounts the whole list for the other type. */}
      <KaryawanTable
        key={type}
        type={type}
        rows={rows}
        isLoading={isLoading}
        showAssignedClasses={type === UserType.Instructor && classAssignmentEnabled}
      />
    </>
  );
};

export default InstructorPage;
